import { useState } from "react";
import fileDownload from "js-file-download";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import { PageContainer } from "@/components/Responsive";
import { PageTitle } from "@/components/Title";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { useApiClient } from "@/hooks/useApiClient";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import type { User } from "@/types/localstorage";
import { getCorporationListSample } from "../api/corporationList";
import { useCorporationList } from "../corporationList/useCorporationList";
import { ResolutionDialog } from "../corporationList/ResolutionDialog";
import { AcquisitionConfirmDialog } from "../corporationList/AcquisitionConfirmDialog";
import { UploadStep } from "../corporationList/UploadStep";
import { MappingStep } from "../corporationList/MappingStep";
import { ConfirmStep } from "../corporationList/ConfirmStep";
import { errorMessage } from "../corporationList/messages";
import { FlowStepper } from "../corporationList/FlowStepper";

const steps = ["ファイルアップロード", "列のマッピング", "法人の確認"];

/** 法人リストから商業登記を一括取得する画面。①アップロード → ②列のマッピング → ③法人の確認の順に切り替える */
export const CorporationList = (): JSX.Element => {
  const { apiClient } = useApiClient();
  const [user] = useLocalStorage<User | undefined>("user", undefined);
  const readOnly = useReadOnlyUser();
  const navigate = useNavigate();
  const state = useCorporationList(apiClient, user?.id ?? "", () => {
    navigate("/commercialbook/list", {
      replace: true,
      state: {
        handOffMessage: { command: "COMMERCIAL_INFO_SHOW_ACCEPT_MESSAGE" },
      },
    });
  });
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [sampleError, setSampleError] = useState("");
  const count = state.rows.filter((row) => row.targetState === "TARGET").length;
  const disabled = state.busy || state.sending || readOnly;
  const uploadDisabled = disabled || !state.settings || !user?.id;

  const downloadSample = async (fileType: "csv" | "xlsx"): Promise<void> => {
    setSampleError("");
    try {
      const data = await getCorporationListSample(apiClient, fileType);
      fileDownload(data, `法人リストサンプル.${fileType}`, data.type);
    } catch (error) {
      setSampleError(errorMessage(error));
    }
  };

  return (
    <PageContainer>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap="13px">
            <Box
              aria-hidden
              sx={{
                position: "relative",
                flexShrink: 0,
                width: 28,
                height: 31,
                display: "grid",
                placeItems: "center",
                borderRadius: "2px",
                bgcolor: "#111",
                color: "#fff",
                fontSize: 15,
                fontWeight: 800,
                // 右上を折り返した書類の形（モックの .title-icon）
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  borderLeft: "8px solid transparent",
                  borderTop: "8px solid white",
                },
              }}
            >
              法
            </Box>
            <PageTitle>法人リストから商業登記を一括取得</PageTitle>
          </Stack>
          <Typography
            sx={{
              mt: "14px",
              ml: { xs: 0, [MOBILE_BREAKPOINT]: "42px" },
              color: "#62696d",
              fontSize: 14,
              lineHeight: 1.8,
            }}
          >
            お手元のCSV・Excelから法人を特定し、確認済みの法人だけを商業登記の一括取得へ進めます。
          </Typography>
        </Box>
        {readOnly && (
          <Alert severity="info">
            現在の権限では取得を依頼できません。過去に送信した依頼の受付状況は確認できます。
          </Alert>
        )}
        {state.settings && !state.settings.acquisitionEnabled && (
          <Alert severity="info">
            法人リストからの取得は現在受け付けていません。
          </Alert>
        )}
        {(state.error || sampleError) && (
          <Alert severity="error" role="alert">
            {state.error || sampleError}
          </Alert>
        )}
        {state.pending && (
          <Alert
            severity="warning"
            action={
              <Button
                disabled={state.sending}
                onClick={() => {
                  void state.lookup();
                }}
              >
                受付状況を確認
              </Button>
            }
          >
            前回の送信結果を確認できていません（
            {new Date(state.pending.sentAt).toLocaleString("ja-JP")}
            ）。同じ受付キーで再確認・再送します。再送する場合は同じファイルと取得条件を使用してください。
          </Alert>
        )}
        {/* Stack の spacing は子要素の左右marginを0で上書きするため、mx: "auto" ではなく外側の flex で中央に寄せる */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <FlowStepper steps={steps} active={step} />
        </Box>
        {state.busy && <LinearProgress aria-label="ファイル解析・法人照合中" />}
        {step === 0 && (
          <UploadStep
            state={state}
            uploadDisabled={uploadDisabled}
            onNext={() => {
              setStep(1);
            }}
            onTrySample={() => {
              void state.uploadSample();
            }}
            onDownloadSample={(fileType) => {
              void downloadSample(fileType);
            }}
          />
        )}
        {step === 1 && (
          <MappingStep
            state={state}
            disabled={disabled}
            onBack={() => {
              setStep(0);
            }}
            onConfirm={() => {
              void state.confirmMapping().then((matched) => {
                if (matched) setStep(2);
              });
            }}
          />
        )}
        {step === 2 && (
          <ConfirmStep
            state={state}
            disabled={disabled}
            acquisitionEnabled={state.settings?.acquisitionEnabled ?? false}
            onBack={() => {
              setStep(1);
            }}
            onResolve={setSelected}
            onOpenConfirm={() => {
              setConfirm(true);
            }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          再読み込み・画面移動で取込中の法人リストは失われます。取得受付後は「取得済商業登記から」で結果を確認できます。
        </Typography>
        <Box>
          <Link
            component={RouterLink}
            to="/mypage"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              ml: "7px",
              color: "#1e6f83",
            }}
          >
            <KeyboardArrowLeft />
            <Typography sx={{ fontSize: 14 }}>マイページに戻る</Typography>
          </Link>
        </Box>
        <ResolutionDialog
          input={state.inputs.find((row) => row.rowNumber === selected) ?? null}
          result={state.rows.find((row) => row.rowNumber === selected) ?? null}
          api={apiClient}
          busy={disabled}
          matchError={state.error}
          close={() => {
            setSelected(null);
          }}
          save={async (change) => {
            return selected !== null
              ? await state.changeRow(selected, change)
              : false;
          }}
        />
        <AcquisitionConfirmDialog
          open={confirm}
          state={state}
          count={count}
          disabled={disabled}
          onClose={() => {
            setConfirm(false);
          }}
        />
      </Stack>
    </PageContainer>
  );
};
